/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Ronnel Bedana - ronnel@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiHeaders } from './headers';
import { RestResponse } from '../model';
import { environment } from 'src/environments/environment';
import { SystemType } from "../model/enums/system.type";
import { GoalData } from "../model/statistics";
import { StatStageType } from '../model/enums/stat.stage.type';
import { QuoteStatsType } from '../model/enums/quote.stats.type';

@Injectable()
export class StatisticsProvider {

  constructor(
    private http: HttpClient
  ) { }

  getStatistics(type: SystemType, offset: number): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/statistics?type=${type}&offset=${offset}`,
        ApiHeaders.getOptions());
  }

  getChartData(type: SystemType, offset: number): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/statistics/chart?type=${type}&offset=${offset}`,
        ApiHeaders.getOptions());
  }

  getQuoteStats(type: SystemType): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/statistics/quote?type=${type}`,
        ApiHeaders.getOptions());
  }

  getChartPredictedData(type: SystemType): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/statistics/chart/predicted?type=${type}`,
        ApiHeaders.getOptions());
  }

  createGoal(type: SystemType, goal: GoalData): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/statistics/goal?type=${type}`,
        goal,
        ApiHeaders.getOptions());
  }

  getReportStats(type: SystemType, offset: number, stage: StatStageType): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/statistics/complete?type=${type}&offset=${offset}&stage=${stage}`,
        ApiHeaders.getOptions());
  }

  getReportStatDueToday(type: SystemType): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/statistics/due-today?type=${type}`,
        ApiHeaders.getOptions());
  }

  getReportStatPastDue(type: SystemType): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/statistics/past-due?type=${type}`,
        ApiHeaders.getOptions());
  }

  getReportStatAnalystOpenFile(type: SystemType): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/statistics/analyst-open-files?type=${type}`,
        ApiHeaders.getOptions());
  }
  getReportStatDueTomorrow(type: SystemType): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/statistics/due-tomorrow?type=${type}`,
        ApiHeaders.getOptions());
  }

  getReportStatAnalystTierErrors(type: SystemType, fromDate: string, toDate: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/statistics/analyst-tier-errors?type=${type}` + (fromDate ? '&fromDate=' + encodeURIComponent(fromDate) : "") + (toDate ? '&toDate=' + encodeURIComponent(toDate) : ""),
        ApiHeaders.getOptions());
  }

  getQuoteData(type: SystemType, stage: QuoteStatsType, fromDate?: string, toDate?: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/statistics/quote/data?type=${type}&stage=${stage}` +
        (fromDate ? `&fromDate=${fromDate}` : '') +
        (toDate ? `&toDate=${toDate}` : ''),
        ApiHeaders.getOptions());
  }

  exportQuoteData(type: SystemType, stage: QuoteStatsType, fromDate?: string, toDate?: string): Observable<any> {
    return this.http
      .post(`${environment.API_URL}/api/statistics/quote/data/export?type=${type}&stage=${stage}` +
        (fromDate ? `&fromDate=${fromDate}` : '') +
        (toDate ? `&toDate=${toDate}` : ''), null, ApiHeaders.getOptionsJsonPlain());
  }

  getFollowUpDueTodayQuote(type: SystemType): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/statistics/quote/followUp-due-today?type=${type}`,
        ApiHeaders.getOptions());

  }
  getFollowUpDueTomorrowQuote(type: SystemType): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/statistics/quote/followUp-due-tomorrow?type=${type}`,
        ApiHeaders.getOptions());
  }

  getReportRevenueStats(type: SystemType, offset: number, stage: StatStageType): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/statistics/complete-revenue?type=${type}&offset=${offset}&stage=${stage}`,
        ApiHeaders.getOptions());
  }


}
